/**
 * Engagement review queue (§12.4 "unverified tasks stay pending", §16.2, §17.4 "every automated exclusion has a
 * reason code, review route and sampled quality review"): pending follow/join attestations with their reason
 * codes, masked participant, evidence (no contacts), QUALIFIED / REJECTED (reason required) decisions.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaskCompletionAdminRowDto, TaskCompletionStatus } from '@windrop/contracts';
import { Button, Field, Select, useIntl } from '@windrop/ui';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ConfirmDestructive } from '../../components/ConfirmDestructive';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { FilterBar } from '../../components/FilterBar';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, humanizeStatus } from '../../components/StatusBadge';
import { isApiError } from '../../lib/api/client';
import { engagementApi } from '../../lib/api/entries';

export function ReviewQueuePage() {
  const [params, setParams] = useSearchParams();
  const intl = useIntl();
  const qc = useQueryClient();
  const status = (params.get('status') ?? 'PENDING') as TaskCompletionStatus | '';
  const lotteryId = params.get('lotteryId') ?? '';
  const q = useQuery({
    queryKey: ['engagement', 'completions', { status, lotteryId }],
    queryFn: () =>
      engagementApi.completions({
        limit: 100,
        ...(status ? { status } : {}),
        ...(lotteryId ? { lotteryId } : {}),
      }),
  });
  const [reject, setReject] = useState<TaskCompletionAdminRowDto | null>(null);
  const review = useMutation({
    mutationFn: ({
      id,
      decision,
      reason,
    }: {
      id: string;
      decision: 'QUALIFIED' | 'REJECTED';
      reason?: string;
    }) => engagementApi.review(id, { status: decision, ...(reason ? { reason } : {}) }),
    onSuccess: () => {
      setReject(null);
      void qc.invalidateQueries({ queryKey: ['engagement', 'completions'] });
    },
  });
  const columns: Column<TaskCompletionAdminRowDto>[] = [
    {
      id: 'created',
      header: 'Received',
      cell: (r) => intl.formatDateTime(r.createdAt),
      sortValue: (r) => r.createdAt,
    },
    {
      id: 'task',
      header: 'Task',
      cell: (r) => `${humanizeStatus(r.type)}${r.platform ? ` · ${humanizeStatus(r.platform)}` : ''}`,
      sortValue: (r) => r.type,
    },
    {
      id: 'participant',
      header: 'Participant',
      cell: (r) => <Link to={`/participants/${r.participantId}`}>{r.displayName}</Link>,
      filterValue: (r) => r.displayName,
    },
    { id: 'contact', header: 'Contact (masked)', cell: (r) => r.contactMasked },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => <StatusBadge kind="TaskCompletionStatus" value={r.status} />,
      sortValue: (r) => r.status,
    },
    {
      id: 'reasons',
      header: 'Reason codes',
      cell: (r) =>
        r.reasonCodes.length ? r.reasonCodes.join(', ') : <span className="wd-muted">none (sampled)</span>,
      filterValue: (r) => r.reasonCodes.join(' '),
    },
    { id: 'bonus', header: 'Bonus', cell: (r) => r.bonusAwarded, align: 'end' },
    {
      id: 'review',
      header: 'Review',
      cell: (r) =>
        r.reviewReason ? (
          `${r.reviewReason}${r.reviewedBy ? ' · staff' : ''}`
        ) : (
          <span className="wd-muted">—</span>
        ),
      hideable: true,
    },
  ];
  if (q.isError)
    return (
      <ErrorState
        title="Review queue unavailable"
        body="Try again or check your permissions."
        reference={isApiError(q.error) ? q.error.supportReference : undefined}
        action={<Button onClick={() => void q.refetch()}>Retry</Button>}
      />
    );
  const rows = q.data?.items ?? [];
  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Engagement review' }]}
        title="Engagement review"
        description="Follow / join attestations held for review by risk signals or sampling. Qualifying awards the bonus now; rejecting keeps the evidence and needs a reason."
      />
      <FilterBar>
        <Field label="Status">
          <Select
            options={[
              { value: 'PENDING', label: 'Pending review' },
              { value: 'QUALIFIED', label: 'Qualified' },
              { value: 'REJECTED', label: 'Rejected' },
              { value: '', label: 'All' },
            ]}
            value={status}
            onChange={(e) =>
              setParams((p) => {
                const n = new URLSearchParams(p);
                if (e.target.value) n.set('status', e.target.value);
                else n.delete('status');
                return n;
              })
            }
            data-testid="review-status-filter"
          />
        </Field>
      </FilterBar>
      <DataTable
        caption="Engagement completions"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.completionId}
        loading={q.isPending}
        emptyState={
          <EmptyState
            title="Nothing to review"
            body="Attestations only land here when risk signals trip or the hourly spot-check samples them."
            action={<Button href="/lotteries">Back to lotteries</Button>}
          />
        }
        initialSort={{ columnId: 'created', direction: 'desc' }}
        density="compact"
        testId="review-table"
        rowActions={(r) =>
          r.status === 'PENDING' ? (
            <div className="wd-btn-group">
              <Button
                variant="primary"
                onClick={() =>
                  review.mutate({ id: r.completionId, decision: 'QUALIFIED', reason: 'Verified by reviewer' })
                }
                loading={review.isPending && review.variables?.id === r.completionId}
                data-testid={`qualify-${r.completionId}`}
              >
                Qualify
              </Button>
              <Button variant="danger" onClick={() => setReject(r)} data-testid={`reject-${r.completionId}`}>
                Reject
              </Button>
            </div>
          ) : null
        }
      />
      <ConfirmDestructive
        open={reject !== null}
        onOpenChange={(o) => {
          if (!o) setReject(null);
        }}
        title="Reject completion"
        objectName={reject?.completionId ?? ''}
        objectKind="completion id"
        consequence="The participant earns no bonus from this task. The evidence and your reason stay on record and can be appealed through support."
        requireReason
        busy={review.isPending}
        error={
          review.isError ? (isApiError(review.error) ? review.error.message : 'Review failed') : undefined
        }
        onConfirm={({ reason }) => {
          if (reject) review.mutate({ id: reject.completionId, decision: 'REJECTED', reason });
        }}
      />
    </>
  );
}
